import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { languageConfig } from "src/common/util/langConfig";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import {
  fetchHelpArticle,
  selectHelpArticleEntry,
} from "src/features/graph/state/helpArticlesSlice";
import type { ILanguage } from "src/model/model";

export interface HelpArticle {
  articleId: string;
  title: string;
  body: string;
  url: string;
}

type HelpArticleStatus = "loading" | "ready" | "none";

interface UseHelpArticleResult {
  status: HelpArticleStatus;
  article: HelpArticle | null;
}

/**
 * The HelpDocs UI language ("en" | "fr") derived from the current user's
 * preference.
 */
export const useHelpArticleLang = (): string => {
  const currentUser = useAppSelector(getCurrentUser);
  return languageConfig((currentUser?.preferred_language as ILanguage)?.name);
};

/**
 * Read the mapped HelpDocs article for a content type from the redux cache,
 * dispatching a fetch if it isn't there yet. The heavy lifting (network + 6h
 * server cache) lives in helpArticlesSlice; the thunk's `condition` skips
 * anything already cached or in flight, so reopening the same node form never
 * refetches.
 */
export const useHelpArticle = (modelName: string): UseHelpArticleResult => {
  const dispatch = useAppDispatch();
  const lang = useHelpArticleLang();
  const entry = useAppSelector(selectHelpArticleEntry(lang, modelName));

  useEffect(() => {
    if (!modelName) return;
    void dispatch(fetchHelpArticle({ modelName, lang }));
  }, [dispatch, modelName, lang]);

  if (!modelName) return { status: "none", article: null };
  if (!entry) return { status: "loading", article: null };
  return { status: entry.status, article: entry.article };
};
